import { getLineItemPaidPriceInCents } from "./paid-price.lib";
import type { DraftLineItem, Order, Shop } from "../types";

export type DiscountBonuses = {
  /** Bonus in cents per line item, only for items resolved as a store credit. */
  byLineItemId: Record<string, number>;
  total: number;
  /** Currency the bonus is expressed in, null when no bonus is configured. */
  currency: string | null;
};

export const EMPTY_BONUSES: DiscountBonuses = {
  byLineItemId: {},
  total: 0,
  currency: null,
};

type Bonus = NonNullable<Shop["discount"]>["discount_bonus"];

function getBonus(shop: Shop | undefined): Bonus | null {
  const bonus = shop?.discount?.discount_bonus;
  return bonus?.amount ? bonus : null;
}

/**
 * Bonus granted on one store-credit article, in cents:
 * - `percentage`: applied to the paid price of the article,
 * - `fixed_amount`: granted once per return, so only when no other article already
 *   carries a store credit (same rule as the legacy `useDiscountForCurrentItem`).
 *
 * The legacy portal computes the percentage on the TTC paid price for the resolution
 * card and on the HT one for the receipt (`use-discount.ts`). We keep the TTC base
 * everywhere so the card and the receipt agree.
 */
function computeItemBonus(
  bonus: Bonus,
  order: Order,
  lineItemId: string,
  quantity: number,
  isFirstDiscountItem: boolean,
): number {
  if (bonus.type === "percentage") {
    const paidPrice = getLineItemPaidPriceInCents(order, lineItemId) * quantity;
    return Math.round((paidPrice * bonus.amount) / 100);
  }
  if (bonus.type === "fixed_amount") {
    return isFirstDiscountItem ? Math.round(bonus.amount * 100) : 0;
  }
  return 0;
}

/** Bonus for every store-credit article already committed to the draft. */
export function computeDiscountBonuses(
  shop: Shop | undefined,
  order: Order | undefined,
  lineItems: DraftLineItem[],
): DiscountBonuses {
  const bonus = getBonus(shop);
  if (!bonus || !order) return EMPTY_BONUSES;

  const discountItems = lineItems.filter((li) => li.resolution === "discount");
  const byLineItemId = Object.fromEntries(
    discountItems.map((li, index) => [
      li.lineItemId,
      computeItemBonus(bonus, order, li.lineItemId, li.quantity, index === 0),
    ]),
  );

  return {
    byLineItemId,
    total: Object.values(byLineItemId).reduce((sum, value) => sum + value, 0),
    currency: bonus.currency ?? null,
  };
}

/**
 * Bonus the customer would get if this article were resolved as a store credit, in cents.
 * Returns null when the shop configures no bonus.
 */
export function computeHypotheticalBonus(
  shop: Shop | undefined,
  order: Order | undefined,
  lineItems: DraftLineItem[],
  lineItemId: string,
): number | null {
  const bonus = getBonus(shop);
  if (!bonus || !order) return null;

  const current = lineItems.find((li) => li.lineItemId === lineItemId);
  const isFirstDiscountItem = !lineItems.some(
    (li) => li.lineItemId !== lineItemId && li.resolution === "discount",
  );

  return computeItemBonus(
    bonus,
    order,
    lineItemId,
    current?.quantity ?? 1,
    isFirstDiscountItem,
  );
}
